package com.dualremotetest

// Game audio. The game loop only ever calls [TankAudio], which never waits for audio: on Android [QueuedAudio] hands
// each request to a background audio thread; in the browser WebTankAudio plays it through Web Audio.

enum class Sfx(val level: Float, val loop: Boolean = false) {
    PLAYER_FIRE(0.7f),
    ENEMY_FIRE(0.3f),
    BRICK_HIT(0.55f),
    STEEL_HIT(0.5f),
    ARMOR_HIT(0.6f),
    ENEMY_EXPLOSION(0.9f),
    PLAYER_EXPLOSION(1f),
    HQ_EXPLOSION(1f),
    POWERUP_APPEAR(0.6f),
    POWERUP_PICKUP(0.85f),
    EXTRA_LIFE(0.9f),
    PAUSE(0.7f),
    TALLY_TICK(0.45f),
    ENGINE_IDLE(0.22f, loop = true),
    ENGINE_MOVE(0.28f, loop = true),
}

/**
 * Music cues. They never overlap: starting one stops the previous one. The game cues are one-shot; [MENU] loops.
 * STAGE_START, GAME_OVER and HIGH_SCORE are the Famicom game's three music pieces. Where they play is partly our
 * choice: GAME_OVER plays on the field as GAME OVER rises (the original plays it on the brick screen after the tally),
 * HIGH_SCORE is also the jingle on the results screen of every cleared stage, and [MENU] (calm loop on the title and
 * menus) is our addition – the original's title screen is silent.
 */
enum class Music(val asset: String, val loops: Boolean = false) {
    NONE(""),
    STAGE_START("stage_start"),
    GAME_OVER("game_over"),
    HIGH_SCORE("high_score"),
    MENU("menu", loops = true),
}

/** What the game calls. Every method must return immediately. */
interface TankAudio {
    fun sfx(s: Sfx)
    /** Starts a music cue from the beginning, stopping any other; [Music.NONE] just stops. */
    fun music(m: Music)
    fun stopMusic() = music(Music.NONE)
    /** True while the one-shot cue [m] (started by [music]) is still sounding; false once it has ended, been replaced or skipped. */
    fun isPlaying(m: Music): Boolean = false
    fun engine(s: EngineSound)
    fun setGamePaused(paused: Boolean)
    fun setVolumes(musicPercent: Int, sfxPercent: Int)
    fun statsText(): String
}

object NoAudio : TankAudio {
    override fun sfx(s: Sfx) = Unit
    override fun music(m: Music) = Unit
    override fun engine(s: EngineSound) = Unit
    override fun setGamePaused(paused: Boolean) = Unit
    override fun setVolumes(musicPercent: Int, sfxPercent: Int) = Unit
    override fun statsText() = "no audio output attached"
}

/** The part that actually makes sound. Runs only on the audio thread and may block (file I/O, MediaPlayer). */
interface AudioBackend {
    fun prepare()
    fun playSfx(s: Sfx, gain: Float)
    /**
     * Starts cue [m]; returns false if it could not be started. [onFinished] is called (on any thread) when a one-shot
     * cue plays to its end; not when it is stopped or replaced.
     */
    fun playMusic(m: Music, gain: Float, onFinished: () -> Unit): Boolean
    fun stopMusic()
    fun setMusicGain(gain: Float)
    /** Starts [s] as the one looping effect (engine), replacing any other; null stops it. */
    fun setLoop(s: Sfx?, gain: Float)
    fun pause()
    fun resume()
    fun release()
    fun describe(): String
}

/** Volume steps and the perceptual gain curve, shared by every [TankAudio] implementation. */
object AudioLevels {
    /** The same effect is not re-triggered within this interval (it would only add noise). */
    const val MIN_REPEAT_NS = 40_000_000L
    val VOLUME_STEPS = listOf(0, 25, 50, 75, 100)

    /** Perceptual volume: the percentage is squared, so 50% is about -12 dB rather than -6 dB. */
    fun gainFor(percent: Int): Float = (percent.coerceIn(0, 100) / 100f).let { it * it }
}

